import { Type, type Static } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { MatrixEventIdSchema } from "./agent-host.ts";
import { ExcelExpertActivityEventSchema } from "./excel-expert-activity.ts";
import { KnowledgeExpertActivityEventSchema } from "./knowledge-expert-activity.ts";
import { PptExpertActivityEventSchema } from "./ppt-expert-activity.ts";
import { BrowserExpertActivityEventSchema } from "./browser-expert.ts";

export const AGENT_ACTIVITY_SNAPSHOT_LIMITS = Object.freeze({
  maxActivities: 64,
  maxAnswerDraftCodePoints: 20_000,
  maxSequence: 1_000_000
});

export const AgentPublicActivityEventSchema = Type.Union([
  KnowledgeExpertActivityEventSchema,
  PptExpertActivityEventSchema,
  ExcelExpertActivityEventSchema,
  BrowserExpertActivityEventSchema
]);

export const AgentActivitySnapshotSchema = Type.Object(
  {
    version: Type.Literal("1.0"),
    sourceEventId: MatrixEventIdSchema,
    throughSequence: Type.Integer({
      minimum: 0,
      maximum: AGENT_ACTIVITY_SNAPSHOT_LIMITS.maxSequence
    }),
    activities: Type.Array(AgentPublicActivityEventSchema, {
      maxItems: AGENT_ACTIVITY_SNAPSHOT_LIMITS.maxActivities
    }),
    answerDraft: Type.String({
      // TypeBox counts UTF-16 code units. Keep a structural code-unit guard here and
      // enforce the public code-point limit in isAgentActivitySnapshotV1.
      maxLength: AGENT_ACTIVITY_SNAPSHOT_LIMITS.maxAnswerDraftCodePoints * 2
    })
  },
  { additionalProperties: false }
);

export type AgentPublicActivityEventV1 = Static<typeof AgentPublicActivityEventSchema>;
export type AgentActivitySnapshotV1 = Static<typeof AgentActivitySnapshotSchema>;

export function isAgentActivitySnapshotV1(value: unknown): value is AgentActivitySnapshotV1 {
  if (!Value.Check(AgentActivitySnapshotSchema, value)) return false;
  let previousSequence = 0;
  for (const activity of value.activities) {
    if (
      activity.sourceEventId !== value.sourceEventId ||
      activity.sequence <= previousSequence ||
      activity.sequence > value.throughSequence
    ) {
      return false;
    }
    previousSequence = activity.sequence;
  }
  return [...value.answerDraft].length <= AGENT_ACTIVITY_SNAPSHOT_LIMITS.maxAnswerDraftCodePoints;
}
